import { t } from '@trimble/i18n';
import {
  AlignmentMeasurementCapability,
  AngleMeasurementCapability,
  LineMeasurementCapability,
  PointMeasurementCapability,
  PolygonMeasurementCapability,
  PolylineMeasurementCapability,
  SimpleMeasurementCapabilityBase,
} from '@trimble/web3d-capabilities';
import {
  MeasurementType,
  MeasurementsCapabilityProvider,
} from '@trimble/web3d-providers';
import {
  Accessor,
  Component,
  Show,
  createMemo,
  createSignal,
  type JSX,
} from 'solid-js';
import {
  BasicButton,
  ControlMenu,
  ControlMenuItemType,
  TrbToolbar,
  ToolbarPositioner,
} from '@trimble/ui-components/solidjs';
import { CustomTooltip } from '@tcweb/connect-ui';
import { useDropdown, useLongPress } from '@trimble/solidjs-utils';
import { SuboptionsIndicator } from './SuboptionsIndicator';
import { SuboptionsDropdown } from './SuboptionsDropdown';

export type GroundworksQualityMetricsMenuPlanType = 'PILE_PLAN' | 'DRILL_PLAN';

export interface MeasurementToolbarProps {
  polylineMeasurementCapability?: PolylineMeasurementCapability;
  lineMeasurementCapability?: LineMeasurementCapability;
  pointMeasurementCapability?: PointMeasurementCapability;
  angleMeasurementCapability?: AngleMeasurementCapability;
  alignmentMeasurementCapability?: AlignmentMeasurementCapability;
  /**
   * When false, the alignment tool button is disabled in this toolbar (e.g. Image pane).
   * Shared capability enablement may still be true when another pane allows alignment measurement.
   */
  alignmentMeasurementUserAllowed?: Accessor<boolean>;
  isAlignmentRailOptionsEnabled?: Accessor<boolean>;
  polygonMeasurementCapability?: PolygonMeasurementCapability;
  supportsVolumetricMeasurements: boolean;
  supportsGroundworksMeasurements: boolean;
  /**
   * When set to a non-empty string, the volumetric control is disabled and this text is the tooltip.
   * Caller supplies localized copy (e.g. from `t(...)`).
   */
  volumetricMeasurementDisabledReason?: string;
  isVolumetricMeasurementActive?: boolean;
  isGroundworksMeasurementActive?: boolean;
  selectedGroundworksPlanType?: Accessor<GroundworksQualityMetricsMenuPlanType | null>;
  measurementsProvider: MeasurementsCapabilityProvider;
  hideDeleteAllButton?: boolean;
  orientation?: 'horizontal' | 'vertical';
  toolbarPositionClasses?: string;
  useToolbarContainer?: boolean;
  onMeasurementToggle?(
    capability: SimpleMeasurementCapabilityBase | AlignmentMeasurementCapability
  ): void;
  onVolumetricMeasurementClick?(): void;
  onGroundworksQualityMetricsSelect?(
    planType: GroundworksQualityMetricsMenuPlanType
  ): void;
  onClearAll?(): void;
  showSuboptions?: boolean;
}

export const MeasurementToolbar: Component<MeasurementToolbarProps> = (
  props
) => {
  const handleMeasurementToggle = (
    measurementCapability:
      | SimpleMeasurementCapabilityBase
      | AlignmentMeasurementCapability
  ): void => {
    if (props.onMeasurementToggle) {
      props.onMeasurementToggle(measurementCapability);
    } else {
      // Default behavior if no handler provided
      if (!measurementCapability.isActive()) {
        void measurementCapability.startMeasurement();
      } else {
        void measurementCapability.stopMeasurement();
      }
    }
  };

  const handleClearAll = (): void => {
    if (props.onClearAll) {
      props.onClearAll();
    } else {
      // Default behavior if no handler provided
      props.measurementsProvider.removeAllMeasurements();
    }
  };

  const suboptionsDropdown = useDropdown();
  const qualityMetricsDropdown = useDropdown();

  const [currentSuboptionsType, setCurrentSuboptionsType] =
    createSignal<MeasurementType | null>(null);

  const qualityMetricsMenuItems = createMemo((): ControlMenuItemType[] => {
    const selected = props.selectedGroundworksPlanType?.() ?? null;
    return [
      {
        id: 'groundworks-category',
        type: 'header',
        label: t(
          'web3D.toolbar.actions.groundworksQualityMetricsMenu.groundworks'
        ),
      },
      {
        id: 'groundworks-piling',
        type: 'action',
        label: t('web3D.toolbar.actions.groundworksQualityMetricsMenu.piling'),
        selected: selected === 'PILE_PLAN',
        onClick: () => {
          props.onGroundworksQualityMetricsSelect?.('PILE_PLAN');
        },
      },
      {
        id: 'groundworks-drilling',
        type: 'action',
        label: t(
          'web3D.toolbar.actions.groundworksQualityMetricsMenu.drilling'
        ),
        selected: selected === 'DRILL_PLAN',
        onClick: () => {
          props.onGroundworksQualityMetricsSelect?.('DRILL_PLAN');
        },
      },
    ];
  });

  const hasOptions = (type: MeasurementType | null): boolean => {
    return type === 'point' || type === 'polyline' || type === 'alignment';
  };

  const openSuboptions = (type: MeasurementType): void => {
    if (suboptionsDropdown.isOpen() && currentSuboptionsType() !== type) {
      suboptionsDropdown.close();
    }

    setCurrentSuboptionsType(type);
    suboptionsDropdown.toggle();
  };

  const pointLongPress = useLongPress({
    enabled: () => hasOptions('point') && !!props.showSuboptions,
    onLongPress: () => openSuboptions('point'),
  });

  const polylineLongPress = useLongPress({
    enabled: () => hasOptions('polyline') && !!props.showSuboptions,
    onLongPress: () => openSuboptions('polyline'),
  });

  const alignmentLongPress = useLongPress({
    enabled: () => hasOptions('alignment') && !!props.showSuboptions,
    onLongPress: () => openSuboptions('alignment'),
  });

  const volumetricDisabledReason = createMemo(
    () => props.volumetricMeasurementDisabledReason?.trim() || undefined
  );

  const renderToolbarContent = (): JSX.Element => (
    <>
      <Show when={props.pointMeasurementCapability}>
        {(capability) => (
          <div
            data-testid="point-measurement-button"
            ref={pointLongPress.ref}
            class="relative"
          >
            <CustomTooltip
              text={t('web3D.toolbar.actions.pointMeasurement')}
              position={props.orientation === 'vertical' ? 'right' : 'bottom'}
              arrow={true}
            >
              <BasicButton
                iconName="tc-icon-fixed-point"
                action={() => {
                  if (!pointLongPress.isLongPressActive()) {
                    handleMeasurementToggle(capability());
                  }
                }}
                disabled={!capability().isEnabled()}
                pressed={capability().isActive()}
                size="small"
              />
            </CustomTooltip>
            <Show when={props.showSuboptions}>
              <SuboptionsIndicator
                measurementType="point"
                onOpenSuboptions={openSuboptions}
                isLongPressActive={pointLongPress.isLongPressActive}
              />
            </Show>
            <SuboptionsDropdown
              type="point"
              isOpen={suboptionsDropdown.isOpen}
              currentType={currentSuboptionsType}
              dropdownRef={suboptionsDropdown.ref}
              measurementsProvider={props.measurementsProvider}
              pointMeasurementCapability={props.pointMeasurementCapability}
              polylineMeasurementCapability={
                props.polylineMeasurementCapability
              }
              onClose={suboptionsDropdown.close}
            />
          </div>
        )}
      </Show>
      <Show when={props.lineMeasurementCapability}>
        {(capability) => (
          <div data-testid="line-measurement-button">
            <CustomTooltip
              text={t('web3D.toolbar.actions.lineMeasurement')}
              position={props.orientation === 'vertical' ? 'right' : 'bottom'}
              arrow={true}
            >
              <BasicButton
                iconName="tc-icon-line"
                action={() => {
                  handleMeasurementToggle(capability());
                }}
                disabled={!capability().isEnabled()}
                pressed={capability().isActive()}
                size="small"
              />
            </CustomTooltip>
          </div>
        )}
      </Show>
      <Show when={props.polylineMeasurementCapability}>
        {(capability) => (
          <div
            data-testid="polyline-measurement-button"
            ref={polylineLongPress.ref}
            class="relative"
          >
            <CustomTooltip
              text={t('web3D.toolbar.actions.polylineMeasurement')}
              position={props.orientation === 'vertical' ? 'right' : 'bottom'}
              arrow={true}
            >
              <BasicButton
                iconName="tc-icon-line"
                action={() => {
                  if (!polylineLongPress.isLongPressActive()) {
                    handleMeasurementToggle(capability());
                  }
                }}
                disabled={!capability().isEnabled()}
                pressed={capability().isActive()}
                size="small"
              />
            </CustomTooltip>
            <Show when={props.showSuboptions}>
              <SuboptionsIndicator
                measurementType="polyline"
                onOpenSuboptions={openSuboptions}
                isLongPressActive={polylineLongPress.isLongPressActive}
              />
            </Show>
            <SuboptionsDropdown
              type="polyline"
              isOpen={suboptionsDropdown.isOpen}
              currentType={currentSuboptionsType}
              dropdownRef={suboptionsDropdown.ref}
              measurementsProvider={props.measurementsProvider}
              pointMeasurementCapability={props.pointMeasurementCapability}
              polylineMeasurementCapability={
                props.polylineMeasurementCapability
              }
              onClose={suboptionsDropdown.close}
            />
          </div>
        )}
      </Show>
      <Show when={props.alignmentMeasurementCapability}>
        {(capability) => (
          <div
            data-testid="alignment-measurement-button"
            ref={alignmentLongPress.ref}
            class="relative"
          >
            <CustomTooltip
              text={t('web3D.toolbar.actions.alignmentMeasurement')}
              position={props.orientation === 'vertical' ? 'right' : 'bottom'}
              arrow={true}
            >
              <BasicButton
                iconName="tc-icon-path-navigatio"
                action={() => {
                  if (!alignmentLongPress.isLongPressActive()) {
                    handleMeasurementToggle(capability());
                  }
                }}
                disabled={
                  !capability().isEnabled() ||
                  (props.alignmentMeasurementUserAllowed !== undefined &&
                    !props.alignmentMeasurementUserAllowed())
                }
                pressed={capability().isActive()}
                size="small"
              />
            </CustomTooltip>
            <Show when={props.showSuboptions}>
              <SuboptionsIndicator
                measurementType="alignment"
                onOpenSuboptions={openSuboptions}
                isLongPressActive={alignmentLongPress.isLongPressActive}
              />
            </Show>
            <SuboptionsDropdown
              type="alignment"
              isOpen={suboptionsDropdown.isOpen}
              currentType={currentSuboptionsType}
              dropdownRef={suboptionsDropdown.ref}
              measurementsProvider={props.measurementsProvider}
              alignmentMeasurementCapability={
                props.alignmentMeasurementCapability
              }
              alignmentOptionsEnabled={props.isAlignmentRailOptionsEnabled}
              onClose={suboptionsDropdown.close}
            />
          </div>
        )}
      </Show>
      <Show when={props.angleMeasurementCapability}>
        {(capability) => (
          <div data-testid="angle-measurement-button">
            <CustomTooltip
              text={t('web3D.toolbar.actions.angleMeasurement')}
              position={props.orientation === 'vertical' ? 'right' : 'bottom'}
              arrow={true}
            >
              <BasicButton
                iconName="tc-icon-measure-angle"
                action={() => {
                  handleMeasurementToggle(capability());
                }}
                disabled={!capability().isEnabled()}
                pressed={capability().isActive()}
                size="small"
              />
            </CustomTooltip>
          </div>
        )}
      </Show>
      <Show when={props.polygonMeasurementCapability}>
        {(capability) => (
          <div data-testid="polygon-measurement-button">
            <CustomTooltip
              text={t('web3D.toolbar.actions.polygonMeasurement')}
              position={props.orientation === 'vertical' ? 'right' : 'bottom'}
              arrow={true}
            >
              <BasicButton
                iconName="tc-icon-measure-area"
                action={() => {
                  handleMeasurementToggle(capability());
                }}
                disabled={!capability().isEnabled()}
                pressed={capability().isActive()}
                size="small"
              />
            </CustomTooltip>
          </div>
        )}
      </Show>
      <Show when={props.supportsVolumetricMeasurements}>
        <div data-testid="volumetric-measurement-button">
          <CustomTooltip
            text={
              volumetricDisabledReason() ??
              t('web3D.toolbar.actions.volumetricMeasurement')
            }
            position={props.orientation === 'vertical' ? 'right' : 'bottom'}
            arrow={true}
          >
            <BasicButton
              iconName="svg-compute-volume"
              action={() => {
                if (!volumetricDisabledReason()) {
                  props.onVolumetricMeasurementClick?.();
                }
              }}
              disabled={!!volumetricDisabledReason()}
              pressed={props.isVolumetricMeasurementActive ?? false}
              size="small"
            />
          </CustomTooltip>
        </div>
      </Show>
      <Show when={props.supportsGroundworksMeasurements}>
        <div
          data-testid="groundworks-test-panel-toolbar-button"
          ref={qualityMetricsDropdown.ref}
          class="relative"
        >
          <CustomTooltip
            text={t('web3D.toolbar.actions.groundworksQualityMetricsPanel')}
            position={props.orientation === 'vertical' ? 'right' : 'bottom'}
            arrow={true}
          >
            <BasicButton
              iconName="svg-groundworks-quality"
              action={() => {
                qualityMetricsDropdown.toggle();
              }}
              pressed={props.isGroundworksMeasurementActive ?? false}
              size="small"
            />
          </CustomTooltip>
          <Show when={qualityMetricsDropdown.isOpen()}>
            <div
              class="absolute left-full top-0 ml-2 shadow z-dropdown"
              onMouseDown={(e) => e.stopPropagation()}
              data-testid="groundworks-quality-metrics-menu"
            >
              <ControlMenu
                items={qualityMetricsMenuItems()}
                onClose={qualityMetricsDropdown.close}
              />
            </div>
          </Show>
        </div>
      </Show>
      <Show when={!props.hideDeleteAllButton}>
        <div data-testid="clear-markup-button">
          <CustomTooltip
            text={t('web3D.toolbar.actions.clearMarkup')}
            position={props.orientation === 'vertical' ? 'right' : 'bottom'}
            arrow={true}
          >
            <BasicButton
              iconName="tc-icon-delete-all"
              action={handleClearAll}
              disabled={!props.measurementsProvider.hasItems()}
              size="small"
            />
          </CustomTooltip>
        </div>
      </Show>
    </>
  );

  const getContainerClasses = (): string => {
    const baseClasses = 'flex gap-1 items-center justify-center';
    const orientationClasses =
      props.orientation === 'vertical' ? 'flex-col' : 'flex-row';
    return `${baseClasses} ${orientationClasses}`;
  };

  const useToolbarContainer = (): boolean => props.useToolbarContainer ?? false;

  return (
    <>
      <Show
        when={useToolbarContainer()}
        fallback={
          <div class={getContainerClasses()} data-testid="measurement-toolbar">
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {renderToolbarContent()}
          </div>
        }
      >
        <div data-testid="measurement-toolbar" class="contents">
          <ToolbarPositioner customClasses={props.toolbarPositionClasses}>
            <TrbToolbar
              orientation="vertical"
              addSeparators={false}
              customClasses="gap-2"
            >
              {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
              {renderToolbarContent()}
            </TrbToolbar>
          </ToolbarPositioner>
        </div>
      </Show>
    </>
  );
};
